/**
 * Bootstrap routine for registering common client-side licenses and other low-level library setup.
 * Common routines to go in this file include:
 *  - TypeScript module augmentation
 *  - AG Grid license registration and feature registration.
 *  - Highcharts feature registration.
 */

//-----------------------------------------------------------------
// App Services -- Import and Register
//-----------------------------------------------------------------
import {XH} from '@xh/hoist/core';
import {when} from '@xh/hoist/mobx';

import {SecMasterService} from './rzx/svc/SecMasterService';
import {LivePositionsService} from './rzx/svc/LivePositionsService';
import {IntradayService} from './rzx/svc/IntradayService';
import {NewsService} from './rzx/svc/NewsService';
import {PortfolioService} from './rzx/svc/PortfolioService';
import {OrderService} from './rzx/svc/OrderService';
import {WatchListService} from './rzx/svc/WatchListService';
import {ChartService} from './rzx/svc/ChartService';
import {MessageHub} from './rzx/svc/MessageHub';
import {ResearchService} from './rzx/svc/ResearchService';
import {WorkflowService} from './rzx/svc/WorkflowService';
import {ExchangeService} from './rzx/svc/ExchangeService';
import {PerformanceService} from './rzx/svc/PerformanceService';
import {EntitlementService} from './rzx/svc/EntitlementService';
import {AiChatService} from './rzx/svc/AiChatService';
import {AiSpecAssistService} from './rzx/svc/AiSpecAssistService';

declare module '@xh/hoist/core' {
    // Merge interface with XHApi class to include injected services.
    export interface XHApi {
        entitlementService: EntitlementService;
        secMasterService: SecMasterService;
        portfolioService: PortfolioService;
        livePositionsService: LivePositionsService;
        /** Desktop only. */
        intradayService: IntradayService;
        newsService: NewsService;
        watchListService: WatchListService;
        chartService: ChartService;
        researchService: ResearchService;
        messageHub: MessageHub;
        workflowService: WorkflowService;
        exchangeService: ExchangeService;
        performanceService: PerformanceService;
        orderService: OrderService;
        aiChatService: AiChatService;
        aiSpecAssistService: AiSpecAssistService;
    }

    export interface HoistUser {
        profilePicUrl: string;
    }
}

//-----------------------------------------------------------------
// ag-Grid - Community features
//-----------------------------------------------------------------
import {installAgGrid} from '@xh/hoist/kit/ag-grid';
import {AgGridReact} from 'ag-grid-react';
import {ModuleRegistry} from 'ag-grid-community';

import {
    ClientSideRowModelApiModule,
    ClientSideRowModelModule,
    CellStyleModule,
    ColumnApiModule,
    CustomEditorModule,
    EventApiModule,
    PinnedRowModule,
    RenderApiModule,
    RowSelectionModule,
    RowApiModule,
    RowAutoHeightModule,
    RowDragModule,
    RowStyleModule,
    ScrollApiModule,
    TextEditorModule,
    TooltipModule
} from 'ag-grid-community';

ModuleRegistry.registerModules([
    ClientSideRowModelApiModule,
    ClientSideRowModelModule,
    CellStyleModule,
    ColumnApiModule,
    CustomEditorModule,
    EventApiModule,
    PinnedRowModule,
    RenderApiModule,
    RowSelectionModule,
    RowApiModule,
    RowAutoHeightModule,
    RowDragModule,
    RowStyleModule,
    ScrollApiModule,
    TextEditorModule,
    TooltipModule
]);

//-----------------------------------------------------------------
// ag-Grid - Enterprise features
//-----------------------------------------------------------------
import {LicenseManager, CellSelectionModule, ClipboardModule, MasterDetailModule, MenuModule, RowGroupingModule, TreeDataModule} from 'ag-grid-enterprise';

ModuleRegistry.registerModules([CellSelectionModule, ClipboardModule, MasterDetailModule, MenuModule, RowGroupingModule, TreeDataModule]);

//-----------------------------------------------------------------
// ag-Grid - App-specific features
//-----------------------------------------------------------------
import {
    SparklinesModule,
    ServerSideRowModelModule,
    SetFilterModule,
    AdvancedFilterModule,
    ExcelExportModule,
    ColumnAutoSizeModule,
    ServerSideRowModelApiModule
} from 'ag-grid-enterprise';
import {TextFilterModule, NumberFilterModule, PaginationModule} from 'ag-grid-community';
import {AgChartsCommunityModule} from 'ag-charts-community';

ModuleRegistry.registerModules([
    SparklinesModule.with(AgChartsCommunityModule),
    ServerSideRowModelModule,
    SetFilterModule,
    TextFilterModule,
    AdvancedFilterModule,
    NumberFilterModule,
    PaginationModule,
    TreeDataModule,
    ExcelExportModule,
    ColumnAutoSizeModule,
    ServerSideRowModelApiModule
]);

installAgGrid(AgGridReact as any, ClientSideRowModelModule.version);

when(
    () => XH.appIsRunning,
    () => {
        const agLicense = XH.getPref('agLicense') as string;
        if (agLicense && agLicense.length > 10) LicenseManager.setLicenseKey(agLicense);
    }
);

//-------------------------------------------------------------------------------
// Highcharts - Import and Register
// You must provide a license for any features (e.g. highstock) that require it
//-------------------------------------------------------------------------------
import {installHighcharts} from '@xh/hoist/kit/highcharts';
import Highcharts from 'highcharts/highstock';
import 'highcharts/modules/exporting';
import 'highcharts/modules/export-data';
import 'highcharts/modules/offline-exporting';
import 'highcharts/modules/heatmap';
import 'highcharts/highcharts-more';

import 'highcharts/modules/treemap';
import 'highcharts/modules/treegraph';
import 'highcharts/modules/hollowcandlestick';
import 'highcharts/modules/heikinashi';
import 'highcharts/indicators/indicators';
import 'highcharts/indicators/indicators-all';
import 'highcharts/modules/drag-panes';
import 'highcharts/modules/annotations-advanced';
import 'highcharts/modules/full-screen';
import 'highcharts/modules/price-indicator';
import 'highcharts/modules/stock-tools';
import {installOhlcSeriesTypes} from './rzx/tabs/charts/impl/OhlcSeriesTypes';

installHighcharts(Highcharts);
installOhlcSeriesTypes();

// Icons - registers app icons with Hoist
import './core/Icons';
